import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import * as authActions from 'global/actions/authActions';
import * as signUpActions from 'global/actions/signUpActions';
import { useActions } from 'hooks/useActions';
import { Input } from 'user/components';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { agreementLabel, GDPRLabel } from 'user/containers/SignupPage/strings';
import { BenefitsList, BenefitsListItem } from './styles';
import { Header, Body, Footer, FooterButton } from 'components/Modals/modalElements';

const english = 'en';

// Get fragment with modal sign up functionality
export default function ModalContentSignUp({ email, setEmail, onCancel, goToLogin }) {
  const { initialize } = useActions(authActions);

  const { loading: signupLoading, error: signupError, success: signupSuccess } = useSelector((state) => state.signUp);
  const { clearError: signupClearError, clearState: signupClearState, signup } = useActions(signUpActions);

  const [isAgreementAgreed, setIsAgreementAgreed] = useState(false);
  const [isGDPRAgreed, setIsGDPRAgreed] = useState(false);
  const [signUpDone, setSignUpDone] = useState(false);

  useEffect(() => {
    signupClearState();
  }, [signupClearState]);

  useEffect(() => {
    if (signupError) {
      toast.error(signupError?.[english] || signupError);
      signupClearError();
    }
  }, [signupError, signupClearError]);

  useEffect(() => {
    if (signupSuccess) {
      setSignUpDone(true);
      let interval = setInterval(() => initialize(), 1000);
      return () => {
        clearInterval(interval);
        signupClearState();
      };
    }
  }, [signupSuccess, setSignUpDone, initialize, signupClearState]);

  const signUpModalBody = () => (
    <>
      <Header>Sign up to get these benefits</Header>
      <Body loading={signupLoading}>
        <BenefitsList>
          <BenefitsListItem>Save your progress as you solve exercises</BenefitsListItem>
          <BenefitsListItem>Earn achievements and experience points</BenefitsListItem>
          <BenefitsListItem>Compete against the community in exciting challenges</BenefitsListItem>
        </BenefitsList>
        <Input type="text" autoFocus value={email} placeholder="Email" onChange={(e) => setEmail(e.target.value)} />
        <Checkbox
          checked={isAgreementAgreed}
          onChange={() => setIsAgreementAgreed(!isAgreementAgreed)}
          label={agreementLabel[english]}
        />
        <Checkbox checked={isGDPRAgreed} onChange={() => setIsGDPRAgreed(!isGDPRAgreed)} label={GDPRLabel[english]} />
        <i>Note: Your current code will remain</i>
      </Body>
      <Footer>
        <FooterButton onClick={onCancel} label="Cancel" />
        <FooterButton onClick={goToLogin} label="Log In" />
        <FooterButton
          onClick={() => signup(email, '', '', false, isAgreementAgreed, isGDPRAgreed)}
          label="Sign up"
          disabled={signupLoading || !email || !isAgreementAgreed || !isGDPRAgreed}
        />
      </Footer>
    </>
  );

  const signUpSuccessBody = () => (
    <>
      <Header>Success</Header>
      <Body>
        <p>
          An email has been sent with instructions on how to complete the signup process. Didn't get the email? Remember
          to check the spam folder
        </p>
        <p>Leave this modal open to automatically sign in when the email instructions have been completed.</p>
      </Body>
      <Footer>
        <FooterButton onClick={onCancel} label="Close" />
      </Footer>
    </>
  );

  return !signUpDone ? signUpModalBody() : signUpSuccessBody();
}
